import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  MenuItem,
  Chip,
  Divider,
  FormControlLabel,
  Checkbox,
  Button,
  CircularProgress,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import RefreshIcon from '@mui/icons-material/Refresh';
import PrintIcon from '@mui/icons-material/Print';
import BlockIcon from '@mui/icons-material/Block';
import ClearIcon from '@mui/icons-material/Clear';
import useUser from '../../hooks/useUser';
import useBranch from '../../hooks/useBranch';
import useAirKey from '../../hooks/useAirKey';
import { isFeatureEnabled } from '../../config/featureFlags';
import AuthContext from '../../context/authContext';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const formatDateTime = d => (d ? dayjs(d).format('DD.MM.YYYY HH:mm') : '—');

export default function EmployeeStatus() {
  const authCtx = useContext(AuthContext);
  const { getUserStatus, startPrintProcess, deactivateUser, deactivateAssignedBranchUser } = useUser();
  const { getBranches } = useBranch();
  const { activateForUser, reactivateForUser } = useAirKey();
  const canActivateAirKey =
    isFeatureEnabled('airkey') &&
    (authCtx.checkRight('airkey:activate') || authCtx.checkRight('airkey:activate:own'));
  const [activatingAirKeyId, setActivatingAirKeyId] = useState(null);
  const [reactivatingAirKeyId, setReactivatingAirKeyId] = useState(null);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [branchId, setBranchId] = useState('');
  const [branches, setBranches] = useState([]);
  const [onlyRegistrationOpen, setOnlyRegistrationOpen] = useState(false);
  const [onlyNeverLoggedIn, setOnlyNeverLoggedIn] = useState(false);
  const [onlyInactive7d, setOnlyInactive7d] = useState(false);
  const [printDialogUser, setPrintDialogUser] = useState(null);
  const [selectedPrintBranchId, setSelectedPrintBranchId] = useState('');
  const [printingUserId, setPrintingUserId] = useState(null);
  const [deactivateDialogUser, setDeactivateDialogUser] = useState(null);
  const [deactivatingUserId, setDeactivatingUserId] = useState(null);
  const canPrintEmployeeCards =
    authCtx.checkRight('user:special:print') || authCtx.checkRight('user:special:printAssignedBranches');
  const canDeactivateEmployees =
    authCtx.checkRight('user:special:deactivate') ||
    authCtx.checkRight('user:special:deactivateAssignedBranches');
  const hasActions = canActivateAirKey || canPrintEmployeeCards || canDeactivateEmployees;

  useEffect(() => {
    const load = async () => {
      const [uRes, bRes] = await Promise.allSettled([
        getUserStatus({ search, branchId }),
        getBranches(),
      ]);

      if (uRes.status === 'fulfilled') setUsers(uRes.value || []);
      else setUsers([]);

      if (bRes.status === 'fulfilled') setBranches(bRes.value || []);
      else setBranches([]);
    };
    load();
  }, [search, branchId]);

  const filteredUsers = useMemo(() => {
    return (users || []).filter(u => {
      const regOpen = !u?.status?.registrationFinished;
      const neverLogged = !u?.status?.lastLogin;
      const inactive7d = !neverLogged && dayjs().diff(dayjs(u.status.lastLogin), 'day') >= 7;
      if (onlyRegistrationOpen && !regOpen) return false;
      if (onlyNeverLoggedIn && !neverLogged) return false;
      if (onlyInactive7d && !inactive7d) return false;
      return true;
    });
  }, [users, onlyRegistrationOpen, onlyNeverLoggedIn, onlyInactive7d]);

  const activeFilterCount = Number(onlyRegistrationOpen) + Number(onlyNeverLoggedIn) + Number(onlyInactive7d);
  const resetFilters = () => {
    setOnlyRegistrationOpen(false);
    setOnlyNeverLoggedIn(false);
    setOnlyInactive7d(false);
  };

  const usersByBranch = useMemo(() => {
    const map = new Map();
    for (const u of filteredUsers) {
      const ids = u.branchIds && u.branchIds.length ? u.branchIds : ['__none__'];
      for (const id of ids) {
        if (!map.has(id)) map.set(id, []);
        map.get(id).push(u);
      }
    }
    return map;
  }, [filteredUsers]);

  const branchName = id => {
    if (id === '__none__') return 'Ohne Filiale';
    const b = branches.find(x => String(x._id) === String(id));
    return b?.longName || b?.locationName || b?.name || id;
  };

  const getUserBranchOptions = user =>
    (user?.branchIds || []).map(id => ({ _id: id, name: branchName(id) })).filter(branch => !!branch._id);

  const openPrintDialog = user => {
    const options = getUserBranchOptions(user);
    setPrintDialogUser(user);
    setSelectedPrintBranchId(options[0]?._id || '');
  };

  const closePrintDialog = () => {
    setPrintDialogUser(null);
    setSelectedPrintBranchId('');
  };

  const closeDeactivateDialog = () => {
    setDeactivateDialogUser(null);
  };

  const reloadUsers = async () => {
    const data = await getUserStatus({ search, branchId });
    setUsers(data || []);
  };

  const handleReprint = async () => {
    if (!printDialogUser || !selectedPrintBranchId) return;
    setPrintingUserId(printDialogUser._id);
    try {
      await startPrintProcess(printDialogUser._id, selectedPrintBranchId);
      toast.success('Mitarbeiterkarten-Nachdruck wurde gestartet');
      closePrintDialog();
      await reloadUsers();
    } catch (err) {
      toast.error('Fehler beim Starten des Mitarbeiterkarten-Nachdrucks');
    } finally {
      setPrintingUserId(null);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateDialogUser) return;
    setDeactivatingUserId(deactivateDialogUser._id);
    try {
      if (authCtx.checkRight('user:special:deactivate')) {
        await deactivateUser({ userId: deactivateDialogUser._id, immediate: true });
      } else {
        await deactivateAssignedBranchUser(deactivateDialogUser._id);
      }
      toast.success('Mitarbeiter wurde deaktiviert');
      closeDeactivateDialog();
      await reloadUsers();
    } catch (err) {
      toast.error('Fehler beim Deaktivieren des Mitarbeiters');
    } finally {
      setDeactivatingUserId(null);
    }
  };

  return (
    <Box sx={{ ...viewRootTextPrimarySx, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant='h5'>Mitarbeiterstatus</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          size='small'
          label='Suche'
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size='small'
          label='Filiale'
          value={branchId}
          onChange={e => setBranchId(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value=''>Alle</MenuItem>
          {branches.map(b => (
            <MenuItem key={b._id} value={b._id}>
              {b.longName || b.locationName || b.name}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          control={
            <Checkbox
              checked={onlyNeverLoggedIn}
              onChange={e => setOnlyNeverLoggedIn(e.target.checked)}
              size='small'
            />
          }
          label='Nur "Nie eingeloggt"'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={onlyRegistrationOpen}
              onChange={e => setOnlyRegistrationOpen(e.target.checked)}
              size='small'
            />
          }
          label='Nur "Registrierung offen"'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={onlyInactive7d}
              onChange={e => setOnlyInactive7d(e.target.checked)}
              size='small'
            />
          }
          label='Nur >7 Tage inaktiv'
        />
        {activeFilterCount > 0 && (
          <Button size='small' color='inherit' startIcon={<ClearIcon />} onClick={resetFilters}>
            Filter zurücksetzen
          </Button>
        )}
        <Typography variant='body2' color='text.secondary' sx={{ alignSelf: 'center', ml: 'auto' }}>
          {activeFilterCount > 0
            ? `${filteredUsers.length} von ${users.length} Mitarbeitern`
            : `${filteredUsers.length} Mitarbeiter`}
        </Typography>
      </Box>

      {[...usersByBranch.keys()].map(bid => (
        <Box key={bid} sx={{ mt: 3 }}>
          <Typography variant='h6' sx={{ mb: 1.5 }}>
            {branchName(bid)}
          </Typography>

          {/* Header for md+ */}
          <Box
            sx={{
              display: { xs: 'none', md: 'grid' },
              gridTemplateColumns: hasActions ? '2fr 1fr 1fr 1.5fr 1fr' : '2fr 1fr 1fr 2fr',
              gap: 1,
              px: 1,
              pb: 0.5,
            }}
          >
            <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 600 }}>
              Name
            </Typography>
            <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 600 }}>
              Letzter Login
            </Typography>
            <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 600 }}>
              Passwort geändert
            </Typography>
            <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 600 }}>
              Registrierung
            </Typography>
            {hasActions && (
              <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 600 }}>
                Aktionen
              </Typography>
            )}
          </Box>
          <Divider sx={{ display: { xs: 'none', md: 'block' }, mb: 1 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {usersByBranch.get(bid).map((u, idx) => {
              const neverLoggedIn = !u.status.lastLogin;
              const inactive7d =
                !neverLoggedIn && dayjs().diff(dayjs(u.status.lastLogin), 'day') >= 7;
              const notFinished = !u.status.registrationFinished;
              const canReprintCard = canPrintEmployeeCards && !!u.cardId;
              return (
                <Box
                  key={u._id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: '1fr 1fr',
                      md: hasActions ? '2fr 1fr 1fr 1.5fr 1fr' : '2fr 1fr 1fr 2fr',
                    },
                    gap: 1,
                    p: 1.25,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    backgroundColor: idx % 2 === 0 ? 'background.default' : 'background.paper',
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>
                      {u.lastName || ''} {u.firstName || ''}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {u.username} · {u.email || '—'}{u.phone ? ` · ${u.phone}` : ''}
                    </Typography>
                    {u.cardId && (
                      <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                        Karte: {u.cardId}
                      </Typography>
                    )}
                  </Box>

                  <Box>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ display: { xs: 'block', md: 'none' } }}
                    >
                      Letzter Login
                    </Typography>
                    {neverLoggedIn ? (
                      <Chip size='small' color='warning' label='Nie eingeloggt' sx={{ mt: 0.5 }} />
                    ) : (
                      <>
                        <Typography>{formatDateTime(u.status.lastLogin)}</Typography>
                        {inactive7d && (
                          <Chip
                            size='small'
                            color='info'
                            label='>7 Tage inaktiv'
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </>
                    )}
                  </Box>

                  <Box>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ display: { xs: 'block', md: 'none' } }}
                    >
                      Passwort geändert
                    </Typography>
                    <Typography>{formatDateTime(u.status.lastPasswordChange)}</Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ display: { xs: 'block', md: 'none' } }}
                    >
                      Registrierung
                    </Typography>
                    {notFinished ? (
                      <Chip
                        size='small'
                        color='error'
                        label='Registrierung offen'
                        sx={{ mt: 0.5 }}
                      />
                    ) : (
                      <Typography>{formatDateTime(u.status.registrationFinished)}</Typography>
                    )}
                  </Box>

                  {hasActions && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {canReprintCard && (
                        <Tooltip title='Mitarbeiterkarte nachdrucken'>
                          <IconButton
                            size='small'
                            color='primary'
                            disabled={printingUserId === u._id}
                            onClick={() => openPrintDialog(u)}
                          >
                            {printingUserId === u._id ? (
                              <CircularProgress size={14} />
                            ) : (
                              <PrintIcon fontSize='small' />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      {canDeactivateEmployees && (
                        <Tooltip title='Mitarbeiter deaktivieren'>
                          <IconButton
                            size='small'
                            color='error'
                            disabled={deactivatingUserId === u._id}
                            onClick={() => setDeactivateDialogUser(u)}
                          >
                            {deactivatingUserId === u._id ? (
                              <CircularProgress size={14} />
                            ) : (
                              <BlockIcon fontSize='small' />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      {canActivateAirKey && (!u.hasRoles ? (
                        <Typography variant='caption' color='text.disabled'>—</Typography>
                      ) : u.airKeyPersonId && u.airKeyHasActiveMedia !== false ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip size='small' color='success' variant='outlined' label='Verbunden' />
                          <Tooltip title='Handy deaktivieren & neu aktivieren (SMS)'>
                            <IconButton
                              size='small'
                              color='warning'
                              disabled={reactivatingAirKeyId === u._id}
                              onClick={async () => {
                                setReactivatingAirKeyId(u._id);
                                try {
                                  const result = await reactivateForUser(u._id);
                                  if (result.success) {
                                    toast.success('AirKey reaktiviert — neue SMS versendet');
                                  } else {
                                    toast.warn(result.errors?.[0] || 'Teilweise reaktiviert');
                                  }
                                } catch (err) {
                                  toast.error('Fehler beim Reaktivieren');
                                } finally {
                                  setReactivatingAirKeyId(null);
                                }
                              }}
                            >
                              {reactivatingAirKeyId === u._id ? <CircularProgress size={14} /> : <RefreshIcon fontSize='small' />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Tooltip title={u.airKeyPersonId ? 'AirKey-Zugang reaktivieren' : 'AirKey-Zugang aktivieren'}>
                          <Button
                            size='small'
                            variant='outlined'
                            color='success'
                            startIcon={
                              activatingAirKeyId === u._id ? <CircularProgress size={14} /> : <VpnKeyIcon />
                            }
                            disabled={activatingAirKeyId === u._id}
                            onClick={async () => {
                              setActivatingAirKeyId(u._id);
                              try {
                                const result = await activateForUser(u._id);
                                if (result.action === 'no-mappings') {
                                  toast.info('Kein AirKey-Mapping für diesen User');
                                } else if (result.success) {
                                  toast.success('AirKey aktiviert');
                                  setUsers(prev =>
                                    prev.map(user =>
                                      user._id === u._id
                                        ? { ...user, airKeyPersonId: result.airKeyPersonId || user.airKeyPersonId || true, airKeyHasActiveMedia: true }
                                        : user
                                    )
                                  );
                                } else {
                                  toast.warn(result.errors?.[0] || 'Teilweise aktiviert');
                                }
                              } catch (err) {
                                toast.error('Fehler beim Aktivieren');
                              } finally {
                                setActivatingAirKeyId(null);
                              }
                            }}
                          >
                            {u.airKeyPersonId ? 'Reaktivieren' : 'Aktivieren'}
                          </Button>
                        </Tooltip>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}

      <Dialog open={!!printDialogUser} onClose={closePrintDialog} maxWidth='sm' fullWidth>
        <DialogTitle>Mitarbeiterkarte nachdrucken</DialogTitle>
        <DialogContent>
          {printDialogUser && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity='warning'>
                Jetzt müssen Mitarbeiterkarten eingelegt sein, nicht die normalen Spielerkarten. Die
                neue Karte wird in Copago erst am Folgetag aktiv, weil der Syncserver nur alle 24
                Stunden läuft.
              </Alert>
              <Typography variant='body2'>
                Nachdruck für {printDialogUser.firstName} {printDialogUser.lastName}
              </Typography>
              {getUserBranchOptions(printDialogUser).length > 1 ? (
                <TextField
                  select
                  size='small'
                  label='Standort'
                  value={selectedPrintBranchId}
                  onChange={e => setSelectedPrintBranchId(e.target.value)}
                  fullWidth
                >
                  {getUserBranchOptions(printDialogUser).map(branch => (
                    <MenuItem key={branch._id} value={branch._id}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </TextField>
              ) : getUserBranchOptions(printDialogUser).length === 1 ? (
                <Typography variant='body2' color='text.secondary'>
                  Standort: {getUserBranchOptions(printDialogUser)[0].name}
                </Typography>
              ) : (
                <Alert severity='error'>Für diesen Mitarbeiter ist kein Standort hinterlegt.</Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePrintDialog} disabled={!!printingUserId}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            onClick={handleReprint}
            disabled={!printDialogUser || !selectedPrintBranchId || !!printingUserId}
            startIcon={printingUserId ? <CircularProgress size={16} /> : <PrintIcon />}
          >
            Nachdruck starten
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deactivateDialogUser}
        onClose={deactivatingUserId ? undefined : closeDeactivateDialog}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Mitarbeiter deaktivieren</DialogTitle>
        <DialogContent>
          {deactivateDialogUser && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity='warning'>
                Der Mitarbeiter wird sofort deaktiviert und verschwindet aus dem offenen Druck.
              </Alert>
              <Typography variant='body2'>
                Möchtest du {deactivateDialogUser.firstName} {deactivateDialogUser.lastName} wirklich
                deaktivieren?
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeactivateDialog} disabled={!!deactivatingUserId}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            color='error'
            onClick={handleDeactivate}
            disabled={!deactivateDialogUser || !!deactivatingUserId}
            startIcon={deactivatingUserId ? <CircularProgress size={16} /> : <BlockIcon />}
          >
            Deaktivieren
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
